import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { Building2, ArrowRight, Mail, Lock, User, Phone, Eye, EyeOff, ArrowLeft, CheckCircle } from 'lucide-react';
import axios from 'axios';
import branding from '@/config/branding';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Investment amount options in Lakhs
const INVESTMENT_AMOUNTS = [
  { label: '1-2 Lakhs', value: '1-2' },
  { label: '3-5 Lakhs', value: '3-5' },
  { label: '6-9 Lakhs', value: '6-9' },
  { label: '10-15 Lakhs', value: '10-15' },
  { label: '16-25 Lakhs', value: '16-25' },
  { label: '25+ Lakhs', value: '25+' },
];

// Minimum SIP amount
const MIN_SIP_AMOUNT = 50001;

export default function SharedDealPage() {
  const navigate = useNavigate();
  const { dealId } = useParams();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userStatus, setUserStatus] = useState(null);
  
  // Flow: 'email' -> 'login' or 'signup'
  const [mode, setMode] = useState('email');
  const [userExists, setUserExists] = useState(false);
  
  // Form states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  
  // Investment preference states
  const [investmentAmounts, setInvestmentAmounts] = useState([]);
  const [wantsSIP, setWantsSIP] = useState(null);
  const [sipFrequency, setSipFrequency] = useState(null);
  const [sipAmount, setSipAmount] = useState('');
  const [sipAmountError, setSipAmountError] = useState('');

  useEffect(() => {
    checkAuthAndFetchDeal();
  }, [dealId]);

  const checkAuthAndFetchDeal = async () => {
    setLoading(true);
    const userEmail = localStorage.getItem('user_email');
    
    // Fetch deal info (public endpoint)
    let dealData = null;
    try {
      const dealResponse = await axios.get(`${API}/deals/${dealId}/public-info`);
      dealData = dealResponse.data;
      setDeal(dealData);
    } catch (error) {
      toast.error('Deal not found or no longer available');
      navigate('/');
      return;
    }
    
    // Check if user is authenticated
    if (userEmail) {
      try {
        const userResponse = await axios.get(`${API}/profile?email=${encodeURIComponent(userEmail)}`);
        setIsAuthenticated(true);
        setUserStatus(userResponse.data.status);
        
        // If approved, redirect to full deal page immediately
        if (userResponse.data.status === 'approved') {
          // Use dealData directly since state update is async
          const actualDealId = dealData?.id || dealId;
          navigate(`/deals/${actualDealId}`, { replace: true });
          return;
        }
      } catch {
        // User not found or error, clear local storage
        localStorage.clear();
      }
    }
    
    setLoading(false);
  };

  const handleCheckEmail = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setCheckingEmail(true);
    try {
      // Check if user exists
      const response = await axios.get(`${API}/auth/check-email?email=${encodeURIComponent(email)}`);
      setUserExists(response.data.exists);
      
      if (response.data.exists) {
        setMode('login');
        toast.success('Welcome back! Please enter your password.');
      } else {
        setMode('signup');
        toast.info('Create an account to view this deal.');
      }
    } catch (error) {
      // If endpoint doesn't exist, default to showing both options
      setMode('signup');
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error('Please enter your password');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username: email,
        password
      });
      
      localStorage.setItem('user_email', response.data.email);
      localStorage.setItem('user_role', response.data.role);
      localStorage.setItem('user_name', `${response.data.first_name} ${response.data.last_name}`);
      
      toast.success('Login successful!');
      
      // Existing users go to full deal page with normal platform access
      // Use the deal's actual ID for consistent routing
      const actualDealId = deal?.id || dealId;
      console.log('Navigating to deal:', actualDealId);
      navigate(`/deals/${actualDealId}`, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed. Please check your password.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!password || !firstName || !lastName || !phone) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (!termsAccepted) {
      toast.error('Please accept the terms and conditions');
      return;
    }

    // Validate investment preferences
    if (investmentAmounts.length === 0) {
      toast.error('Please select at least one investment amount range');
      return;
    }

    if (wantsSIP === null) {
      toast.error('Please indicate if you want to invest in SIP structure');
      return;
    }

    if (wantsSIP === 'yes') {
      if (!sipFrequency) {
        toast.error('Please select SIP frequency (Monthly or Quarterly)');
        return;
      }
      if (!sipAmount) {
        toast.error('Please enter SIP amount');
        return;
      }
      if (parseInt(sipAmount) < MIN_SIP_AMOUNT) {
        toast.error('SIP amount is below the minimum limit');
        return;
      }
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/signup`, {
        email,
        password,
        phone,
        first_name: firstName,
        last_name: lastName,
        terms_accepted: termsAccepted,
        invited_deal_id: deal?.id || dealId,
        // Investment preference fields
        investment_amounts: investmentAmounts,
        wants_sip: wantsSIP === 'yes',
        sip_frequency: wantsSIP === 'yes' ? sipFrequency : null,
        sip_amount: wantsSIP === 'yes' && sipAmount ? parseInt(sipAmount) : null
      });
      
      localStorage.setItem('user_email', email);
      localStorage.setItem('user_name', `${firstName} ${lastName}`);
      localStorage.setItem('invited_deal_id', deal?.id || dealId);
      
      toast.success('Account created! Your access is pending approval.');
      setIsAuthenticated(true);
      setUserStatus('pending');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setSubmitting(false);
    }
  };

  const resetToEmail = () => {
    setMode('email');
    setPassword('');
    setConfirmPassword('');
    setFirstName('');
    setLastName('');
    setPhone('');
    setTermsAccepted(false);
    // Reset investment preferences
    setInvestmentAmounts([]);
    setWantsSIP(null);
    setSipFrequency(null);
    setSipAmount('');
    setSipAmountError('');
  };

  // Handle investment amount checkbox toggle
  const handleInvestmentAmountToggle = (value) => {
    setInvestmentAmounts(prev => 
      prev.includes(value) 
        ? prev.filter(v => v !== value)
        : [...prev, value]
    );
  };

  // Validate SIP amount
  const validateSipAmount = (amount) => {
    if (amount && parseInt(amount) < MIN_SIP_AMOUNT) {
      setSipAmountError('Amount is below the minimum limit');
      return false;
    }
    setSipAmountError('');
    return true;
  };

  const handleSipAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setSipAmount(value);
    if (value) {
      validateSipAmount(value);
    } else {
      setSipAmountError('');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0F172A] to-[#1E293B] flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  // User is authenticated but pending approval - show deal with restricted view
  if (isAuthenticated && userStatus === 'pending') {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        {/* Header */}
        <header className="bg-white border-b border-[#E2E8F0]">
          <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-brand rounded-lg flex items-center justify-center">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <span className="font-heading font-bold text-[#0F172A]">{branding.platformName.toUpperCase()}</span>
            </div>
            <Badge className="bg-amber-100 text-amber-700 border border-amber-300">
              Pending Approval
            </Badge>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Pending Notice */}
          <Card className="border-amber-200 bg-amber-50 mb-6">
            <CardContent className="p-4">
              <p className="text-amber-800 text-sm">
                <strong>Your account is pending approval.</strong> You can view this deal&apos;s basic information. 
                Once approved, you&apos;ll have full access to all deals and platform features.
              </p>
            </CardContent>
          </Card>

          {/* Deal Card */}
          <Card className="border-[#E2E8F0] shadow-lg">
            <CardHeader className="bg-gradient-to-r from-brand to-brand-hover text-white rounded-t-lg">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center">
                  <Building2 className="h-8 w-8 text-white" />
                </div>
                <div>
                  <CardTitle className="text-2xl font-bold">{deal?.brand_name || deal?.name}</CardTitle>
                  <CardDescription className="text-blue-100">{deal?.type}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-[#0F172A] mb-2">About</h3>
                  <p className="text-[#64748B]">{deal?.one_liner || deal?.description || 'No description available'}</p>
                </div>
                
                {deal?.stage && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#64748B]">Stage:</span>
                    <Badge variant="outline">{deal.stage}</Badge>
                  </div>
                )}
                
                {deal?.category && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#64748B]">Category:</span>
                    <Badge variant="outline">{deal.category}</Badge>
                  </div>
                )}

                {deal?.amount_raising > 0 && (
                  <div className="bg-[#F0FDF4] p-4 rounded-lg">
                    <p className="text-sm text-[#166534]">Raising</p>
                    <p className="text-2xl font-bold text-[#10B981]">
                      ₹{deal.amount_raising?.toLocaleString('en-IN')}
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-6 pt-6 border-t border-[#E2E8F0]">
                <p className="text-sm text-[#64748B] text-center">
                  Full details including pitch deck and commitment options will be available after approval.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Not authenticated - show deal preview with login/signup flow
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-brand rounded-lg flex items-center justify-center">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <span className="font-heading font-bold text-[#0F172A] text-xl">{branding.platformName.toUpperCase()}</span>
          </Link>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* Deal Preview */}
          <Card className="border border-[#E2E8F0] shadow-lg">
            <CardHeader className="bg-gradient-to-r from-brand to-brand-hover text-white rounded-t-lg">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center">
                  <Building2 className="h-8 w-8 text-white" />
                </div>
                <div>
                  <Badge className="bg-white/20 text-white mb-2">{deal?.type}</Badge>
                  <CardTitle className="text-2xl font-bold">{deal?.brand_name || deal?.name}</CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-[#64748B] mb-4">{deal?.one_liner || deal?.description}</p>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                {deal?.stage && (
                  <div>
                    <p className="text-xs text-[#94A3B8]">Stage</p>
                    <p className="font-medium text-[#0F172A]">{deal.stage}</p>
                  </div>
                )}
                {deal?.category && (
                  <div>
                    <p className="text-xs text-[#94A3B8]">Category</p>
                    <p className="font-medium text-[#0F172A]">{deal.category}</p>
                  </div>
                )}
              </div>

              {deal?.amount_raising > 0 && (
                <div className="bg-gradient-to-r from-[#10B981] to-[#059669] p-4 rounded-lg text-white">
                  <p className="text-sm opacity-80">Raising</p>
                  <p className="text-3xl font-bold">₹{deal.amount_raising?.toLocaleString('en-IN')}</p>
                </div>
              )}

              <div className="mt-6 pt-4 border-t border-[#E2E8F0]">
                <p className="text-sm text-[#64748B] text-center">
                  Enter your email to view full details and commit capital
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Auth Forms */}
          <Card className="border border-[#E2E8F0] shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-[#0F172A]">
                {mode === 'email' && 'Access This Deal'}
                {mode === 'login' && 'Welcome Back!'}
                {mode === 'signup' && 'Create Your Account'}
              </CardTitle>
              <CardDescription className="text-[#64748B]">
                {mode === 'email' && 'Enter your email to continue'}
                {mode === 'login' && 'Enter your password to sign in'}
                {mode === 'signup' && 'Fill in your details to get started'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Step 1: Email Check */}
              {mode === 'email' && (
                <form onSubmit={handleCheckEmail} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="pl-10 h-12"
                        autoFocus
                      />
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    disabled={checkingEmail}
                    className="w-full bg-brand hover:bg-brand-hover text-white h-12"
                  >
                    {checkingEmail ? 'Checking...' : 'Continue'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </form>
              )}

              {/* Step 2a: Login (existing user) */}
              {mode === 'login' && (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg p-3 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-[#10B981]" />
                    <span className="text-sm text-[#166534]">Account found for <strong>{email}</strong></span>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        className="pl-10 pr-10 h-12"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#94A3B8] hover:text-[#64748B]"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={submitting}
                    className="w-full bg-brand hover:bg-brand-hover text-white h-12"
                  >
                    {submitting ? 'Signing in...' : 'Sign In'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  
                  <button 
                    type="button" 
                    onClick={resetToEmail} 
                    className="w-full text-sm text-[#64748B] hover:text-[#0F172A] flex items-center justify-center gap-1"
                  >
                    <ArrowLeft className="h-3 w-3" />
                    Use a different email
                  </button>
                </form>
              )}

              {/* Step 2b: Signup (new user) */}
              {mode === 'signup' && (
                <form onSubmit={handleSignup} className="space-y-4">
                  <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-lg p-3">
                    <span className="text-sm text-[#1E40AF]">Creating account for <strong>{email}</strong></span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>First Name *</Label>
                      <Input
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="John"
                        className="h-11"
                        autoFocus
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Last Name *</Label>
                      <Input
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Doe"
                        className="h-11"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Phone Number *</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                      <Input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+91 9876543210"
                        className="pl-10 h-11"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Password *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Min. 6 characters"
                        className="pl-10 pr-10 h-11"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#94A3B8] hover:text-[#64748B]"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Confirm Password *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Re-enter your password"
                        className="pl-10 h-11"
                      />
                    </div>
                  </div>

                  {/* Investment Amount Selection */}
                  <div className="space-y-3 pt-2">
                    <Label className="text-[#0F172A] font-medium text-sm">
                      How much would you like to invest per startup? *
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      {INVESTMENT_AMOUNTS.map((option) => (
                        <div 
                          key={option.value} 
                          className="flex items-center space-x-2"
                        >
                          <Checkbox
                            id={`shared-investment-${option.value}`}
                            checked={investmentAmounts.includes(option.value)}
                            onCheckedChange={() => handleInvestmentAmountToggle(option.value)}
                            className="border-[#E2E8F0] data-[state=checked]:bg-brand"
                          />
                          <label 
                            htmlFor={`shared-investment-${option.value}`} 
                            className="text-sm text-[#0F172A] cursor-pointer"
                          >
                            {option.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* SIP Investment Question */}
                  <div className="space-y-3 pt-2">
                    <Label className="text-[#0F172A] font-medium text-sm">
                      Do you also want to invest in Startup in SIP structure? *
                    </Label>
                    <RadioGroup 
                      value={wantsSIP || ''} 
                      onValueChange={(value) => {
                        setWantsSIP(value);
                        if (value === 'no') {
                          setSipFrequency(null);
                          setSipAmount('');
                          setSipAmountError('');
                        }
                      }}
                      className="flex gap-6"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="shared-sip-yes" className="border-[#E2E8F0] text-brand" />
                        <label htmlFor="shared-sip-yes" className="text-sm text-[#0F172A] cursor-pointer">Yes</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="shared-sip-no" className="border-[#E2E8F0] text-brand" />
                        <label htmlFor="shared-sip-no" className="text-sm text-[#0F172A] cursor-pointer">No</label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Conditional SIP Fields */}
                  {wantsSIP === 'yes' && (
                    <>
                      <div className="space-y-3 pt-2 pl-4 border-l-2 border-brand/20">
                        <Label className="text-[#0F172A] font-medium text-sm">
                          SIP Frequency *
                        </Label>
                        <RadioGroup 
                          value={sipFrequency || ''} 
                          onValueChange={(value) => {
                            setSipFrequency(value);
                            if (sipAmount) {
                              validateSipAmount(sipAmount);
                            }
                          }}
                          className="flex gap-6"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="monthly" id="shared-freq-monthly" className="border-[#E2E8F0] text-brand" />
                            <label htmlFor="shared-freq-monthly" className="text-sm text-[#0F172A] cursor-pointer">Monthly</label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="quarterly" id="shared-freq-quarterly" className="border-[#E2E8F0] text-brand" />
                            <label htmlFor="shared-freq-quarterly" className="text-sm text-[#0F172A] cursor-pointer">Quarterly</label>
                          </div>
                        </RadioGroup>
                      </div>

                      <div className="space-y-2 pl-4 border-l-2 border-brand/20">
                        <Label className="text-[#0F172A] font-medium text-sm">
                          SIP Amount (INR) *
                        </Label>
                        <Input
                          type="text"
                          inputMode="numeric"
                          placeholder="Enter amount"
                          value={sipAmount}
                          onChange={handleSipAmountChange}
                          className={`bg-white border-[#E2E8F0] h-10 text-sm ${sipAmountError ? 'border-red-500' : ''}`}
                        />
                        {sipAmountError && (
                          <p className="text-red-500 text-xs">{sipAmountError}</p>
                        )}
                        {sipAmount && !sipAmountError && (
                          <p className="text-[#64748B] text-xs">
                            ₹{parseInt(sipAmount).toLocaleString('en-IN')}
                          </p>
                        )}
                      </div>
                    </>
                  )}
                  
                  <div className="flex items-start space-x-2 pt-2">
                    <Checkbox
                      id="terms"
                      checked={termsAccepted}
                      onCheckedChange={(checked) => setTermsAccepted(checked)}
                      className="mt-1"
                    />
                    <label htmlFor="terms" className="text-sm text-[#64748B] leading-tight cursor-pointer">
                      I agree to the{' '}
                      <a href="#" className="text-brand hover:underline">Terms of Service</a>
                      {' '}and{' '}
                      <a href="#" className="text-brand hover:underline">Privacy Policy</a>
                    </label>
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={submitting || !termsAccepted}
                    className="w-full bg-brand hover:bg-brand-hover text-white h-11"
                  >
                    {submitting ? 'Creating Account...' : 'Create Account'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  
                  <p className="text-center text-sm text-[#64748B]">
                    Already have an account?{' '}
                    <button type="button" onClick={() => setMode('login')} className="text-brand hover:underline font-medium">
                      Login
                    </button>
                  </p>
                  
                  <button 
                    type="button" 
                    onClick={resetToEmail} 
                    className="w-full text-sm text-[#64748B] hover:text-[#0F172A] flex items-center justify-center gap-1"
                  >
                    <ArrowLeft className="h-3 w-3" />
                    Use a different email
                  </button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
